import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { BASE_URL } from "@/config/env.config";

export const EMPTY_ARRAY: readonly never[] = Object.freeze([]);

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

type CurrencyOptions = {
  fallback?: string | null;
  currency?: string;
  notation?: Intl.NumberFormatOptions["notation"];
  maximumFractionDigits?: number;
};

export function formatCurrency(value: unknown, options: CurrencyOptions = {}): string | null {
  const { fallback = null, currency = "USD", notation = "standard", maximumFractionDigits = 0 } = options;
  if (value == null || value === "" || !Number.isFinite(Number(value))) return fallback;
  return new Intl.NumberFormat("en-US", { style: "currency", currency, notation, maximumFractionDigits }).format(Number(value));
}

/**
 * A bare "YYYY-MM-DD" is a calendar day, not a UTC instant. Parsing it as UTC
 * midnight lands it on the previous calendar day for every user west of
 * Greenwich, which is how a due-diligence window ending *today* came to read as
 * already expired. Bare dates therefore resolve to LOCAL midnight — or local
 * 23:59:59 when `endOfDay` — so countdown math matches the user's wall clock.
 * Full ISO timestamps carry their own offset and pass through untouched.
 */
export function parseIsoDate(value: unknown, endOfDay = false): Date | null {
  if (!value) return null;
  const raw = String(value);
  if (raw.length === 10) {
    const [year, month, day] = raw.split("-").map(Number);
    const bare = endOfDay ? new Date(year, month - 1, day, 23, 59, 59) : new Date(year, month - 1, day);
    return Number.isNaN(bare.getTime()) ? null : bare;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Display-only date formatting, anchored in UTC. Deliberately does NOT reuse
 * `parseIsoDate`: it renders with `timeZone: "UTC"`, so it needs a UTC-anchored
 * Date for parse and render to agree on a calendar day. Callers doing countdown
 * *arithmetic* want `parseIsoDate` + `utcDayDiff`, which are local by design.
 */
export function formatUtcDate(
  value: unknown,
  options: Intl.DateTimeFormatOptions = {},
  fallback = "",
): string {
  if (!value) return fallback;
  const raw = String(value);
  const date = new Date(raw.length === 10 ? `${raw}T23:59:59Z` : raw);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC", ...options });
}

/**
 * Whole calendar days from `now` to `target`, measured on the LOCAL calendar.
 * Local (not UTC) date parts are what make a deadline ending "today" return 0
 * for every user instead of -1 for those west of Greenwich. The name is kept
 * for its callers; the arithmetic is local on purpose.
 */
export function utcDayDiff(target: Date | null, now = new Date()): number | null {
  if (!target || !now) return null;
  const targetDay = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
  const nowDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const difference = Math.round((targetDay - nowDay) / 86_400_000);
  return Number.isFinite(difference) ? difference : null;
}

export function formatRelativeTime(value: unknown, fallback = "Recently"): string {
  if (!value) return fallback;
  const elapsed = Date.now() - new Date(String(value)).getTime();
  if (!Number.isFinite(elapsed) || elapsed < 0) return fallback;
  const minutes = Math.max(1, Math.floor(elapsed / 60_000));
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/** Absolute date + time, e.g. "Aug 3, 2026 at 9:00 AM". For records where the
 *  exact moment matters — a sign-in you are trying to recognise — rather than a
 *  relative "2h ago". */
export function formatDateTime(value: unknown, fallback = "—"): string {
  if (!value) return fallback;
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return fallback;
  return `${date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })} at ${date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}`;
}

export function formatCompactMoney(value: unknown): string {
  return formatCurrency(value, { notation: "compact", maximumFractionDigits: 1, fallback: "$0" }) || "$0";
}

export function formatFileSize(bytes: unknown): string {
  const size = Number(bytes);
  if (!size) return "—";
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

export function getInitials(name: unknown): string {
  return String(name || "")
    .trim().split(/\s+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]?.toUpperCase()).join("") || "?";
}

const AVATAR_COLORS = ["#3e7655", "#536c7a", "#b77934", "#a65c4a", "#24372c"];

export function getAvatarColor(name: unknown): string {
  const str = String(name || "");
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = (hash * 31 + str.charCodeAt(i)) | 0;
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

export function getTimeGreeting(now = new Date()): string {
  const hour = now.getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export function absoluteUrl(value: unknown): string {
  if (!value) return "";
  const url = String(value);
  if (url.startsWith("http")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  return `${BASE_URL}${path}`;
}

export function buildQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") queryParams.append(key, String(value));
  });
  const queryString = queryParams.toString();
  return queryString ? `?${queryString}` : "";
}

export function downloadCsv({ rows, filename }: { rows: unknown[][]; filename: string }): void {
  const escapeCell = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const csv = rows.map((row) => row.map(escapeCell).join(",")).join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
